import { useEffect, useState, type ReactNode } from 'react';
import { api, formatBytes } from '../lib/api.js';

/**
 * Database housekeeping.
 *
 * Rows that no longer describe anything accumulate where nobody looks: tags
 * whose assets were purged, people with no faces left, upload sessions
 * abandoned when a tab closed, a change feed that only grows. Individually
 * none of it matters, which is exactly why it needs a page rather than a query
 * someone has to remember to run.
 *
 * Survey first, always. Nothing here removes anything a user can see, but it
 * is still deletion, and a count on screen beforehand is what makes it a
 * decision rather than a hopeful button press.
 */

type Survey = Awaited<ReturnType<typeof api.maintenanceSurvey>>['categories'];

export function MaintenancePanel(): ReactNode {
  const [categories, setCategories] = useState<Survey | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const load = (): void => {
    setError(null);
    void api
      .maintenanceSurvey()
      .then((res) => setCategories(res.categories))
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not survey'));
  };

  useEffect(load, []);

  const toggle = (key: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const removable = (categories ?? []).filter((c) => c.count > 0);
  const chosen = removable.filter((c) => selected.has(c.category));
  const totalRows = chosen.reduce((sum, c) => sum + c.count, 0);

  const run = async (): Promise<void> => {
    if (chosen.length === 0) return;
    if (
      !confirm(
        `Remove ${totalRows.toLocaleString()} row(s) across ${chosen.length} categor` +
          `${chosen.length === 1 ? 'y' : 'ies'}? No media is deleted.`,
      )
    ) {
      return;
    }

    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.maintenanceRun(chosen.map((c) => c.category));
      const removed = Object.entries(res.removed)
        .filter(([, n]) => n > 0)
        .map(([key, n]) => `${n.toLocaleString()} ${key}`)
        .join(', ');
      setResult(
        `Removed ${removed || 'nothing'}` +
          (res.bytesReclaimed > 0 ? ` — ${formatBytes(res.bytesReclaimed)} freed` : ''),
      );
      setSelected(new Set());
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Maintenance failed');
    } finally {
      setRunning(false);
    }
  };

  if (error && !categories) return <div className="notice error">{error}</div>;
  if (!categories) return <p className="muted">Surveying…</p>;

  return (
    <div>
      <h3>Database maintenance</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        Rows that no longer describe anything. Nothing here removes media, and
        nothing a user can see disappears — to delete files, use{' '}
        <strong>Trash</strong> instead.
      </p>

      {error && <div className="notice error">{error}</div>}
      {result && <div className="notice success">{result}</div>}

      {removable.length === 0 ? (
        <div className="notice success">Nothing to clean up.</div>
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th style={{ width: 34 }} />
                <th>Category</th>
                <th style={{ width: 100 }}>Rows</th>
              </tr>
            </thead>
            <tbody>
              {removable.map((c) => (
                <tr key={c.category}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(c.category)}
                      onChange={() => toggle(c.category)}
                      disabled={running}
                      aria-label={c.label}
                    />
                  </td>
                  <td>
                    <div>{c.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                      {c.description}
                    </div>
                  </td>
                  <td>{c.count.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex" style={{ marginTop: 12 }}>
            <button
              className="btn danger"
              onClick={() => void run()}
              disabled={running || chosen.length === 0}
            >
              {running
                ? 'Cleaning…'
                : `Remove ${totalRows.toLocaleString()} row(s)`}
            </button>
            <button className="btn" onClick={load} disabled={running}>
              Re-survey
            </button>
          </div>
        </>
      )}
    </div>
  );
}
